import { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import moment from 'moment';
import Dialog from '@material-ui/core/Dialog';
import Button from '@material-ui/core/Button';
import { TableColumn, TableWithCollapse } from '../../../lib/DataTable';
import { useTableWithCollapse } from '../../../lib/DataTable/customHooks';
import { XIRR_HISTORY_DATA } from '../../../redux-store/types/nct';
import { LIST_PROTFOLIO_PAYLOAD } from '../../../redux-store/types/portfolio';
import NoFoliosFound from '../makeNct/NoFoliosFound';
import { fileDownload } from '../../../lib/FileDownload';
import { maskPan } from '../../../utils/utilityFunctions';
import SegmentedControl from '../../common/segmented-control';
import { CustomRadio } from '../../../lib/CustomRadio';
import en from '../../../lang/en-us';

const { sebi } = en;

const SUB_TABS = ['Request Statement', 'My Downloads'];
const FREQUENCY_OPTIONS = [
    { label: 'Monthly', value: 'M' },
    { label: 'Half-Yearly', value: 'Y' },
];

interface SebiXirrProps extends RouteComponentProps {
    pan: string;
    mobile: string;
    pekrn: string;
    reqId: string;
    generateXirrReport: Function;
    getXirrHistory: Function;
    getXirrDocument: Function;
}

const CustomDownloadButton = ({ row, downloadXirrDocument }: any) => {
    const isDocumentReadyToDownload = row?.requestStatus === 'Completed';
    const color = isDocumentReadyToDownload ? '#33dbb3' : '#2D9CDB';
    const handleClick = () => {
        if (isDocumentReadyToDownload && !row?.isDownloading) {
            downloadXirrDocument(row);
        }
    };
    return (
        <div
            className="sebi-download"
            role="button"
            style={isDocumentReadyToDownload ? { cursor: 'pointer', color } : { color }}
            onClick={handleClick}
        >
            <img
                src={`/images/${
                    isDocumentReadyToDownload ? 'download-icon.svg' : 'inprogress_icon.svg'
                }`}
                alt="icon"
            />
            {isDocumentReadyToDownload
                ? row?.isDownloading
                    ? 'Downloading'
                    : 'Download'
                : 'In progress'}
        </div>
    );
};

export default function SebiXirr(props: SebiXirrProps) {
    const [activeSubTab, setActiveSubTab] = useState<number>(0);
    const [frequency, setFrequency] = useState<string>('M'); // Default: Monthly
    const [isGenerating, setIsGenerating] = useState<boolean>(false);
    const [isXirrHistoryLoading, setIsXirrHistoryLoading] = useState<boolean>(false);
    const [xirrHistoryData, setXirrHistoryData] = useState<XIRR_HISTORY_DATA[]>([]);
    const [successModal, setSuccessModal] = useState<boolean>(false);

    const downloadColumns: TableColumn[] = [
        {
            headerName: 'S.NO',
            field: 'id',
            valueGetter: ({ index = 0 }) =>
                (index + 1).toLocaleString('en-US', {
                    minimumIntegerDigits: 2,
                    useGrouping: false,
                }),
        },
        {
            headerName: 'REQUESTED DATE & TIME',
            field: 'requestDateTime',
            valueGetter: ({ row }) =>
                row?.requestDateTime
                    ? moment(row.requestDateTime).format('DD-MM-YYYY hh:mm A')
                    : 'N/A',
        },
        {
            headerName: 'STATUS',
            field: 'requestStatus',
            valueGetter: ({ row }) => row?.requestStatus || 'N/A',
        },
        {
            headerName: 'ACTION',
            field: 'action',
            renderCell: ({ row }) => (
                <CustomDownloadButton row={row} downloadXirrDocument={downloadXirrDoc} />
            ),
        },
    ];

    const { tableRows, tableColumns, ...rest } = useTableWithCollapse<XIRR_HISTORY_DATA[]>({
        rows: xirrHistoryData,
        columns: downloadColumns,
        loading: isXirrHistoryLoading,
    });

    const fetchXirrHistory = async () => {
        setIsXirrHistoryLoading(true);
        try {
            const { pan = '', pekrn = '', mobile = '', reqId = '' } = props;
            const payload: LIST_PROTFOLIO_PAYLOAD = {
                pan,
                mobile,
                pekrn,
                reqId,
            };
            const response = await props.getXirrHistory(payload);
            const xirrHistoryResponse = response?.body || {};
            const xirrHistory = xirrHistoryResponse?.history || [];
            setXirrHistoryData(
                xirrHistory
                    .map((obj: {}) => ({ ...obj, isDownloading: false }))
                    .sort(
                        (a: XIRR_HISTORY_DATA, b: XIRR_HISTORY_DATA) =>
                            new Date(b.requestDateTime).getTime() -
                            new Date(a.requestDateTime).getTime(),
                    ),
            );
        } catch (error: any) {
            alert(error?.message || 'Failed to fetch XIRR history');
        } finally {
            setIsXirrHistoryLoading(false);
        }
    };

    useEffect(() => {
        fetchXirrHistory();
    }, []);

    const generateXirr = async () => {
        try {
            setIsGenerating(true);
            const { pan = '', pekrn = '', mobile = '', reqId = '' } = props;
            const payload = {
                pan,
                mobile,
                pekrn,
                reqId,
                frequency,
            };
            const response = await props.generateXirrReport(payload);
            if (response?.status === 202) {
                setSuccessModal(true);
                fetchXirrHistory();
                setActiveSubTab(1); // Switch to My Downloads tab
            }
        } catch (error: any) {
            alert(error?.message || 'Failed to generate XIRR statement');
        } finally {
            setIsGenerating(false);
        }
    };

    const downloadXirrDoc = async (clickedRow: XIRR_HISTORY_DATA) => {
        if (!clickedRow?.documentId) {
            alert('Invalid document');
            return;
        }
        const clickedDoc = xirrHistoryData.find(
            (row) => row.documentId === clickedRow.documentId,
        );
        if (!clickedDoc) {
            alert('Document not found');
            return;
        }
        clickedDoc.isDownloading = true;
        setXirrHistoryData([...xirrHistoryData]);
        try {
            const { pan = '', pekrn = '', mobile = '', reqId = '' } = props;
            const payload = {
                pan,
                pekrn,
                mobile,
                reqId,
                documentId: clickedRow.documentId,
            };
            const response = await props.getXirrDocument(payload);
            if (response?.body) {
                // Backend returns base64-encoded PDF for XIRR - decode to binary
                const base64Text = await response.body.text();
                const binaryString = atob(base64Text);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }
                const pdfData = new Blob([bytes], { type: 'application/pdf' });
                return fileDownload({
                    data: pdfData,
                    mime: 'application/pdf',
                    filename: `xirr_statement_${moment().format('YYYY_MM_DD_HHmmss')}.pdf`,
                });
            }
        } catch (error: any) {
            alert(error?.message || 'Failed to download XIRR statement');
        } finally {
            clickedDoc.isDownloading = false;
            setXirrHistoryData([...xirrHistoryData]);
        }
    };

    const handleSubTabChange = (index: number) => {
        setActiveSubTab(index);
        if (index === 1) {
            fetchXirrHistory();
        }
    };

    const renderRequestTab = () => (
        <>
            <div style={{ paddingTop: 15 }}>
                <p className="sebi-content-left-text">Please select the frequency</p>
            </div>
            <div className="sebi-content-bottom" style={{ paddingTop: 5 }}>
                <CustomRadio
                    name="frequency"
                    extraClass="custom-radio"
                    formGroupClass="frequency-radio-group"
                    list={FREQUENCY_OPTIONS}
                    selectedValue={frequency}
                    handleRadioChange={(value: string) => setFrequency(value)}
                />
            </div>
            <div className="sebi-content-bottom" style={{ paddingTop: 15, paddingBottom: 15 }}>
                <div className="sebi-content-left" style={{ minHeight: 'auto' }}>
                    <p className="sebi-content-left-text">PAN</p>
                </div>
                <div className="sebi-content-right">
                    <p className="sebi-content-text" style={{ color: '#5367FF' }}>
                        {maskPan(props.pan)}
                    </p>
                </div>
            </div>
            <div className="sebi-download-btn" style={{ marginTop: '30px', marginBottom: '0px' }}>
                {isGenerating ? (
                    <a className="customBtn">
                        {sebi.submitting}
                        <span className="sending-dots">
                            <i></i>
                            <i></i>
                            <i></i>
                        </span>
                    </a>
                ) : (
                    <a onClick={generateXirr}>Generate XIRR Statement</a>
                )}
            </div>
            <div style={{ paddingBottom: '60px', marginTop: '-10px' }}>
                <p
                    style={{
                        textAlign: 'center',
                        color: '#666',
                        fontSize: '14px',
                        marginTop: '0px',
                        marginBottom: '0px',
                    }}
                >
                    Statement is generated as PDF. Track and download it from My Downloads.
                </p>
            </div>
        </>
    );

    const renderDownloadsTab = () => (
        <>
            <div className="sebi-content-bottom" style={{ paddingTop: 15, paddingBottom: 10 }}>
                <div className="sebi-content-left" style={{ minHeight: 'auto' }}>
                    <p className="sebi-content-left-text">PAN</p>
                </div>
                <div className="sebi-content-right">
                    <p className="sebi-content-text" style={{ color: '#5367FF' }}>
                        {maskPan(props.pan)}
                    </p>
                </div>
            </div>
            <div className="sebi-portfolio-downloads" style={{ marginTop: '20px' }}>
                {!!tableRows.length || isXirrHistoryLoading ? (
                    <TableWithCollapse columns={tableColumns} rows={tableRows} {...rest} />
                ) : (
                    <NoFoliosFound info={'No Downloads Found'} />
                )}
            </div>
        </>
    );

    return (
        <div className="curved-bg-content curved-bg-table-outer sebi-cas">
            <SegmentedControl
                additionalClasses="portfolio-details-tabs portfolio-details-main-tab"
                segments={SUB_TABS}
                selectedSegmentIndex={activeSubTab}
                onSegmentChanged={handleSubTabChange}
                key={activeSubTab}
            />
            {activeSubTab === 0 ? renderRequestTab() : renderDownloadsTab()}
            <Dialog
                maxWidth={'sm'}
                open={successModal}
                aria-labelledby="note-modals"
            >
                <div className="note-modals">
                    <h3>Note</h3>
                    <p>
                        XIRR statement generation request submitted successfully. Please check My
                        Downloads section after some time.
                    </p>
                    <div className="note-modals-btns">
                        <Button onClick={() => setSuccessModal(false)} className="customBtn">
                            Okay
                        </Button>
                    </div>
                </div>
            </Dialog>
        </div>
    );
}
